import { localize } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import Button from './ui/FitButton';
import { needsRelease105Update } from '../utils/release105Access';
import WebviewMessenger from '../utils/WebviewMessenger';
import { analyticsId } from '../utils/productAnalytics';
import React, { useEffect, useState } from 'react';
import { Box, Skeleton } from '@mui/material';

import { getDeviceUuid, getEntitlements } from '../api';
import { readAlertRuntimeInfo } from '../utils/alertMethodSupport';
import { supportsAccountFeatures } from '../utils/accountAccess';
import { getEntitlementRevision } from '../utils/adRequests';
import { trackProduct } from '../utils/productAnalytics';
import DiscoveryCarousel from './DiscoveryCarousel';
import useAppLanguage from '../i18n';

export default function HomeDiscovery({ medications, familySummary, unreadReplies, onOpenReview, topSpacing = 0.75 }) {
    const navigate = useNavigate();
    useEffect(() => {
        const requestId = analyticsId();
        let done = false;
        const record = (state = 'unknown', fullscreen) => {
            if (done) return; done = true;
            trackProduct(`notification_${state}`, { source: 'home' });
            if (fullscreen === true || fullscreen === false) trackProduct(fullscreen ? 'fullscreen_ready' : 'fullscreen_blocked', { source: 'home' });
        };
        const off = WebviewMessenger.onMessage(message => {
            if (message?.type === 'ANALYTICS_READINESS' && message.requestId === requestId) {
                record(message.granted === true ? 'ready' : message.granted === false ? 'blocked' : 'unknown', message.fullscreen);
            }
        });
        try { WebviewMessenger.send({ type: 'REQUEST_ANALYTICS_READINESS', requestId }); }
        catch { record(); } // An unavailable native bridge must not prevent the home screen from rendering.
        const timer = setTimeout(() => record(), 5000);
        return () => { clearTimeout(timer); off(); };
    }, []);
    const [promotionContext, setPromotionContext] = useState(null);
    const { runtime, adFree } = promotionContext || {};
    useEffect(() => {
        let sent = false;
        const record = () => { if (!sent && document.visibilityState !== 'hidden') { sent = true; trackProduct('home_view', { source: 'home' }); } };
        record(); document.addEventListener('visibilitychange', record);
        return () => document.removeEventListener('visibilitychange', record);
    }, []);
    useEffect(() => {
        let live = true;
        let request = 0;
        const refresh = async () => {
            const currentRequest = ++request;
            const uuid = getDeviceUuid();
            const revision = getEntitlementRevision();
            let nextAdFree = null;
            try { nextAdFree = (await getEntitlements(uuid))?.adFree ?? null; }
            catch {} // Failed lookup still allows the non-purchase promotions.
            if (!live || currentRequest !== request || uuid !== getDeviceUuid()) return;
            if (revision !== getEntitlementRevision()) {
                void refresh();
                return;
            }
            // Publish runtime and entitlement together so the initial list is complete.
            const nextRuntime = readAlertRuntimeInfo();
            setPromotionContext(previous => ({
                uuid,
                revision,
                runtime: nextRuntime,
                adFree: typeof nextAdFree === 'boolean' ? nextAdFree
                    : previous?.uuid === uuid && previous.revision === revision ? previous.adFree : null,
            }));
        };
        refresh();
        window.addEventListener('app-runtime-info-updated', refresh);
        return () => { live = false; window.removeEventListener('app-runtime-info-updated', refresh); };
    }, []);
    const eligible = supportsAccountFeatures(runtime) && runtime?.capabilities?.adFreePurchase === true && adFree === false;
    const awaitingPromotionContext = !promotionContext && medications.length > 0
        && Boolean(window.IS_NATIVE_APP || window.ReactNativeWebView);
    useEffect(() => { if (eligible) trackProduct('purchase_eligible', { feature: 'ad_free', source: 'home' }); }, [eligible]);
    const connected = ['B', 'C', 'W'].includes(familySummary?.state);
    const slides = [];
    if (!medications.length) slides.push({ promoId: 'first_medication', feature: 'medication', state: 'I', onClick: () => navigate('/medications/new-bundle?mode=manual') });
    if ((eligible || (needsRelease105Update(runtime) && adFree === false)) && medications.length) slides.push({ promoId: 'ad_free', feature: 'ad_free', state: 'H', onClick: () => navigate('/settings?adFree=1') });
    slides.push({
        promoId: 'family', feature: 'family', state: familySummary?.state === 'B' ? 'B' : 'A',
        creativeVersion: familySummary?.state === 'B' ? 'family-alert-v1' : connected ? 'family-status-v1' : 'family-intro-v1',
        connected, primary: familySummary?.primary, extraCount: familySummary?.extraCount,
        connectedSubtitle: familySummary?.sharingPaused ? localize('가족 현황 공유가 중단됐어요') : familySummary?.state === 'W' ? localize('나를 돌보는 가족 보기') : undefined,
        onClick: () => navigate(connected ? (familySummary?.route || '/guardian') : '/family-care'),
    });
    if (medications.length) slides.push({ promoId: 'prn', feature: 'prn', state: 'C', onClick: () => navigate(medications.some(m => m.frequencyType === 'AS_NEEDED') ? '/prn' : '/medications/new-bundle?mode=manual&frequency=AS_NEEDED') });
    return <>
        {!awaitingPromotionContext ? <DiscoveryCarousel slides={slides.slice(0, 3)} topSpacing={topSpacing} /> : (
            <Box role="status" aria-label={localize("기능 안내 불러오는 중")} sx={{ mx: 2, mt: topSpacing, pb: '46px' }}>
                <Skeleton variant="rounded" animation={false} height={76} sx={{ borderRadius: '14px' }} />
            </Box>
        )}
        {unreadReplies > 0 && <Box sx={{ mx: 2, mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {unreadReplies > 0 && <Button onClick={onOpenReview} sx={{ color: '#666', minHeight: 44 }}>{localize('건의 답변이 도착했어요')}</Button>}
        </Box>}
    </>;
}
